import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { ClientService } from './client.service';
import { PermissionsService } from './permissions.service';
import { UserProfile } from '../models/user.model';
import { ClientRelationship } from '../models/relationship.model';

/**
 * Service to manage the context of which client a Trustee or Read-Only user is currently viewing/acting for.
 * This enables the "viewing as" functionality for sub-role users.
 */
@Injectable({
    providedIn: 'root',
})
export class ActingContextService {
    private authService = inject(AuthService);
    private clientService = inject(ClientService);
    private permissionsService = inject(PermissionsService);

    // The client ID that the current user is acting on behalf of (if any)
    private _actingForClientId = signal<string | null>(null);

    // The relationship for the current acting context
    private _currentRelationship = signal<ClientRelationship | null>(null);

    // The client profile we're acting for
    private _actingForClient = signal<UserProfile | null>(null);

    // Public readonly signals
    readonly actingForClientId = this._actingForClientId.asReadonly();
    readonly currentRelationship = this._currentRelationship.asReadonly();
    readonly actingForClient = this._actingForClient.asReadonly();

    // Computed: Are we currently acting on behalf of another client?
    readonly isActingForAnother = computed(() => {
        const currentUser = this.authService.userProfile();
        const actingFor = this._actingForClientId();
        return !!actingFor && actingFor !== currentUser?.uid;
    });

    // Computed: Effective user (either self or the client we're acting for)
    readonly effectiveUserId = computed(() => {
        const actingFor = this._actingForClientId();
        if (actingFor) return actingFor;
        return this.authService.userProfile()?.uid || null;
    });

    // Computed: Can the current user commit?
    readonly canCommit = computed(() => {
        const user = this.authService.userProfile();

        // No user, no commit
        if (!user) return false;

        // Read-Only users can never commit
        if (user.subRole === 'ReadOnly') return false;

        // If not acting for another, can commit (already checked not ReadOnly)
        if (!this.isActingForAnother()) {
            return true;
        }

        // If acting for another, check relationship permissions
        const relationship = this._currentRelationship();
        return relationship?.canCommit ?? false;
    });

    // Computed: Can download documents?
    readonly canDownloadDocuments = computed(() => {
        const user = this.authService.userProfile();
        if (!user) return false;

        if (!this.isActingForAnother()) return true;

        const relationship = this._currentRelationship();
        return relationship?.canDownloadDocuments ?? false;
    });

    constructor() {
        // Load initial state from sessionStorage
        const storedClientId = sessionStorage.getItem('actingForClientId');
        if (storedClientId) {
            this.setActingForClient(storedClientId);
        }

        // Clear acting context when user logs out
        effect(() => {
            const user = this.authService.userProfile();
            if (!user) {
                this.clearActingContext();
            }
        });
    }

    /**
     * Set the client we're currently acting on behalf of
     */
    async setActingForClient(clientId: string): Promise<boolean> {
        const currentUser = this.authService.userProfile();

        if (!currentUser) {
            console.error('No authenticated user');
            return false;
        }

        // If setting to self, just clear
        if (clientId === currentUser.uid) {
            this.clearActingContext();
            return true;
        }

        // Check if we have permission to act for this client
        const permissionResult = await this.permissionsService.canPerformAction(
            currentUser.uid,
            clientId,
            'view'
        );

        if (!permissionResult.allowed) {
            console.error('No permission to act for this client:', permissionResult.reason);
            return false;
        }

        // Load client profile
        const clientProfile = await this.clientService.getUserProfile(clientId);
        if (!clientProfile) {
            console.error('Client profile not found');
            return false;
        }

        // Set the context
        this._actingForClientId.set(clientId);
        this._currentRelationship.set(permissionResult.relationship || null);
        this._actingForClient.set(clientProfile);

        // Persist to session storage
        sessionStorage.setItem('actingForClientId', clientId);

        // Log the activity
        await this.permissionsService.logActivity(
            clientId,
            'profile_viewed',
            {
                viewedBy: currentUser.displayName,
                viewedByEmail: currentUser.email,
                timestamp: new Date().toISOString(),
            }
        );

        return true;
    }

    /**
     * Clear the acting context (return to viewing as self)
     */
    clearActingContext(): void {
        this._actingForClientId.set(null);
        this._currentRelationship.set(null);
        this._actingForClient.set(null);
        sessionStorage.removeItem('actingForClientId');
    }

    /**
     * Get the display name for the current context
     */
    getContextDisplayName(): string {
        if (this.isActingForAnother()) {
            const client = this._actingForClient();
            return client?.displayName || 'Unknown Client';
        }
        return this.authService.userProfile()?.displayName || 'You';
    }
}
